import { LOOKUP_FALLBACKS } from "./lookups";
import { todayIndiaIsoDate } from "./indiaTime";

export function emptySalesContractForm() {
  return {
    contract_date: todayIndiaIsoDate(),
    product_family: "RICE",
    pricing_mode: "FIXED_PER_QTL",
    market_condition: "",
    bran_process: "PARBOILED",
    bag_cut_jute_kg: "0",
    bag_cut_ppe_kg: "0.150",
    bardana_gm: "",
    bardana_basis: "PER_BAG",
    crop_id: "",
    type_id: "",
    product_id: "",
    item_group_id: "",
    item_id: "",
    variety: "",
    quality_type: "STEAMED",
    grade: "WAND",
    quantity: "",
    rate: "",
    cd_applicable: false,
    cd_percent: "",
    payment_condition: "SAME_DAY",
    payment_condition_other: "",
    broker_applicable: false,
    broker_party_id: "",
    broker_name: "",
    brokerage_basis: "GROSS_PCT",
    brokerage: "",
    packing: "PP_PACKING",
    buyer_packing_text: "",
    buyer_party_id: "",
    buyer_name: "",
    delivery_condition: "TRUCK_LOAD",
    delivery_terms_other: "",
    notes: "",
  };
}

export function contractToForm(c) {
  if (!c) return emptySalesContractForm();
  const packing = c.packing === "PP_50KG" ? "PP_PACKING" : (c.packing || "PP_PACKING");
  return {
    contract_date: c.contract_date || todayIndiaIsoDate(),
    product_family: c.product_family || "RICE",
    pricing_mode: c.pricing_mode || "FIXED_PER_QTL",
    market_condition: c.market_condition || "",
    bran_process: c.bran_process || (c.quality_type === "STEAMED" ? "STEAM" : "PARBOILED"),
    bag_cut_jute_kg: c.bag_cut_jute_kg != null ? String(c.bag_cut_jute_kg) : "0",
    bag_cut_ppe_kg: c.bag_cut_ppe_kg != null ? String(c.bag_cut_ppe_kg) : "0.150",
    bardana_gm: c.bardana_gm != null ? String(c.bardana_gm) : "",
    bardana_basis: c.bardana_basis || "PER_BAG",
    crop_id: c.crop_id ? String(c.crop_id) : "",
    type_id: c.type_id ? String(c.type_id) : "",
    product_id: c.product_id ? String(c.product_id) : "",
    item_group_id: c.item_group_id ? String(c.item_group_id) : "",
    item_id: c.item_id ? String(c.item_id) : "",
    variety: c.variety || c.item_name || "",
    quality_type: c.quality_type || "STEAMED",
    grade: c.grade || "WAND",
    quantity: c.quantity != null ? String(c.quantity) : "",
    rate: c.rate != null ? String(c.rate) : "",
    cd_applicable: Boolean(c.cd_applicable),
    cd_percent: c.cd_percent != null ? String(c.cd_percent) : "",
    payment_condition: c.payment_condition || "SAME_DAY",
    payment_condition_other: c.payment_condition_other || "",
    broker_applicable: Boolean(c.broker_applicable),
    broker_party_id: c.broker_party_id ? String(c.broker_party_id) : "",
    broker_name: c.broker_name || "",
    brokerage_basis: c.brokerage_basis || "GROSS_PCT",
    brokerage: c.brokerage != null ? String(c.brokerage) : "",
    packing,
    buyer_packing_text: c.buyer_packing_text || "",
    buyer_party_id: c.buyer_party_id ? String(c.buyer_party_id) : "",
    buyer_name: c.buyer_name || "",
    delivery_condition: c.delivery_condition || "TRUCK_LOAD",
    delivery_terms_other: c.delivery_terms_other || "",
    notes: c.notes || "",
  };
}

export function labelFor(options, value) {
  return options.find((o) => o.value === value)?.label ?? value ?? "—";
}

export function varietyLabel(row) {
  return row.item_name || row.variety_label || row.variety || "—";
}

export function qualityTypeLabel(row, options = LOOKUP_FALLBACKS.rice_quality_type) {
  if (row.product_family === "RICE_BRAN") {
    if (row.bran_process === "STEAM") return "Steam bran";
    if (row.bran_process === "PARBOILED") return "Parboiled / sella bran";
  }
  if (row.quality_type_label) return row.quality_type_label;
  return labelFor(options, row.quality_type);
}

export function gradeLabel(row, options = LOOKUP_FALLBACKS.rice_grade) {
  if (row.product_family === "RICE_BRAN") return "—";
  if (row.grade_label) return row.grade_label;
  return labelFor(options, row.grade);
}

export function quantityLabel(row) {
  return `${row.quantity ?? "—"} qtl`;
}

export function rateLabel(row) {
  if (row.rate == null) return "—";
  if (row.pricing_mode === "PER_OIL_POINT") return `₹${row.rate}/oil-pt`;
  return `₹${row.rate}/qtl`;
}

export function paymentLabel(row) {
  if (row.payment_condition === "OTHER") return row.payment_condition_other || "Other";
  return (
    LOOKUP_FALLBACKS.payment_condition.find((o) => o.value === row.payment_condition)?.label
    || row.payment_condition_label
    || row.payment_condition
    || "—"
  );
}

export function deliveryLabel(row) {
  if (row.delivery_condition === "OTHER") return row.delivery_terms_other || "Other";
  const options = LOOKUP_FALLBACKS.sales_delivery_condition || [];
  return options.find((o) => o.value === row.delivery_condition)?.label
    || row.delivery_condition_label
    || row.delivery_condition
    || "—";
}

export function packingLabel(row, options = LOOKUP_FALLBACKS.rice_packing) {
  const code = row.packing === "PP_50KG" ? "PP_PACKING" : row.packing;
  const base = row.packing_label
    || labelFor(options, code)
    || labelFor(options, row.packing)
    || code
    || "—";
  if (row.packing === "BUYER_PACKING" && row.buyer_packing_text) {
    return `${base}: ${row.buyer_packing_text}`;
  }
  return base;
}

/** Bag-cut defaults driven by packing (bran). */
export function branBagCutsForPacking(packing) {
  const code = packing === "PP_50KG" ? "PP_PACKING" : packing;
  if (code === "PP_PACKING") return { bag_cut_jute_kg: "0", bag_cut_ppe_kg: "0.150" };
  if (code === "JUTE_BAG") return { bag_cut_jute_kg: "1.5", bag_cut_ppe_kg: "0" };
  return { bag_cut_jute_kg: "0", bag_cut_ppe_kg: "0" };
}

export function isPpPacking(packing) {
  return packing === "PP_PACKING" || packing === "PP_50KG";
}

export const RICE_BARDANA_GM_OPTIONS = [
  { value: "150", label: "150 gm" },
  { value: "200", label: "200 gm" },
  { value: "300", label: "300 gm" },
];

export const RICE_BARDANA_BASIS_OPTIONS = [
  { value: "PER_BAG", label: "Per bag" },
  { value: "PER_QTL", label: "Per qtl" },
];

export function bardanaLabel(row) {
  if (row.bardana_gm == null || !row.bardana_basis) return "—";
  const basis = row.bardana_basis === "PER_QTL" ? "per qtl" : "per bag";
  return `${row.bardana_gm} gm ${basis}`;
}

export function buyerLabel(row) {
  return row.buyer_display_name || row.buyer_name || "—";
}

export function brokerLabel(row) {
  if (!row.broker_applicable) return "—";
  const name = row.broker_party_name || row.broker_name || "—";
  if (row.brokerage == null || row.brokerage === "") return name;
  if (row.brokerage_basis === "GROSS_PCT") return `${name} · ${row.brokerage}% gross`;
  if (row.brokerage_basis === "PER_QTL") return `${name} · ₹${row.brokerage}/qtl`;
  return name;
}

export function productSummary(row) {
  const arch = [row.crop_name, row.rice_type_name, row.product_name, row.item_group_name, row.item_name]
    .filter(Boolean);
  if (arch.length) return arch.join(" · ");
  if (row.product_family === "RICE_BRAN") {
    return [
      "Rice bran",
      qualityTypeLabel(row),
      row.market_condition || null,
      row.pricing_mode === "PER_OIL_POINT" ? "Oil-point" : "Fixed/qtl",
    ].filter(Boolean).join(" · ");
  }
  return [varietyLabel(row), qualityTypeLabel(row)].filter(Boolean).join(" · ");
}

export function validateSalesContractForm(form) {
  if (!form.buyer_party_id && !String(form.buyer_name || "").trim()) return "Buyer is required";
  if (!form.crop_id) return "Crop is required";
  if (!form.type_id) return "Rice type is required";
  if (!form.product_id) return "Product is required";
  if (!form.item_group_id) return "Item group is required";
  if (!form.item_id) return "Item is required";
  const isBran = form.product_family === "RICE_BRAN";
  if (isBran) {
    if (!form.bran_process) return "Bran process (steam / parboiled) is required";
    if (form.pricing_mode === "PER_OIL_POINT" && !form.market_condition) {
      return "Market condition (UP / Haryana) is required for oil-point pricing";
    }
  }
  if (!form.quantity || Number(form.quantity) <= 0) return "Quantity must be greater than zero";
  if (!form.rate || Number(form.rate) <= 0) return "Rate must be greater than zero";
  if (!form.packing) return "Packing is required";
  if (form.packing === "BUYER_PACKING" && !String(form.buyer_packing_text || "").trim()) {
    return "Describe buyer packing";
  }
  if (!isBran && isPpPacking(form.packing) && form.bardana_gm) {
    if (!["150", "200", "300", 150, 200, 300].map(String).includes(String(form.bardana_gm))) {
      return "Bardana must be 150, 200, or 300 gm";
    }
    if (!form.bardana_basis) return "Select bardana basis (per bag or per qtl)";
  }
  if (form.cd_applicable && !form.cd_percent) return "Select cash discount %";
  if (form.payment_condition === "OTHER" && !String(form.payment_condition_other || "").trim()) {
    return "Enter payment terms for Other";
  }
  if (form.delivery_condition === "OTHER" && !String(form.delivery_terms_other || "").trim()) {
    return "Enter delivery terms for Other";
  }
  if (form.broker_applicable) {
    if (!form.broker_party_id && !String(form.broker_name || "").trim()) return "Broker is required";
    if (!form.brokerage_basis) return "Brokerage basis is required";
    if (form.brokerage === "" || form.brokerage == null) return "Brokerage value is required";
  }
  return null;
}

export function buildSalesContractPayload(form) {
  const isBran = form.product_family === "RICE_BRAN";
  const payload = {
    contract_date: form.contract_date || todayIndiaIsoDate(),
    product_family: form.product_family || "RICE",
    pricing_mode: form.pricing_mode || "FIXED_PER_QTL",
    crop_id: Number(form.crop_id),
    type_id: Number(form.type_id),
    product_id: Number(form.product_id),
    item_group_id: Number(form.item_group_id),
    item_id: Number(form.item_id),
    quantity: Number(form.quantity),
    rate: Number(form.rate),
    cd_applicable: Boolean(form.cd_applicable),
    payment_condition: form.payment_condition,
    broker_applicable: Boolean(form.broker_applicable),
    packing: form.packing,
    delivery_condition: form.delivery_condition,
    notes: String(form.notes || "").trim() || null,
  };
  if (isBran) {
    payload.bran_process = form.bran_process || "PARBOILED";
    payload.quality_type = form.bran_process === "STEAM" ? "STEAMED" : "WHITE_SELLA";
    payload.grade = "WAND"; // kept for DB; not collected on form
    if (form.pricing_mode === "PER_OIL_POINT") {
      payload.market_condition = form.market_condition;
    }
    const cuts = branBagCutsForPacking(form.packing);
    payload.bag_cut_jute_kg = Number(form.bag_cut_jute_kg ?? cuts.bag_cut_jute_kg);
    payload.bag_cut_ppe_kg = Number(form.bag_cut_ppe_kg ?? cuts.bag_cut_ppe_kg);
  } else {
    payload.quality_type = form.quality_type || "STEAMED";
    payload.grade = "WAND";
    if (isPpPacking(form.packing) && form.bardana_gm) {
      payload.bardana_gm = Number(form.bardana_gm);
      payload.bardana_basis = form.bardana_basis || "PER_BAG";
    } else {
      payload.bardana_gm = null;
      payload.bardana_basis = null;
    }
  }
  if (payload.cd_applicable) payload.cd_percent = Number(form.cd_percent);
  if (payload.payment_condition === "OTHER") {
    payload.payment_condition_other = String(form.payment_condition_other || "").trim();
  }
  if (payload.broker_applicable) {
    if (form.broker_party_id) payload.broker_party_id = Number(form.broker_party_id);
    else payload.broker_name = String(form.broker_name || "").trim();
    payload.brokerage_basis = form.brokerage_basis;
    payload.brokerage = Number(form.brokerage);
  }
  if (form.buyer_party_id) payload.buyer_party_id = Number(form.buyer_party_id);
  else payload.buyer_name = String(form.buyer_name || "").trim();
  if (payload.packing === "BUYER_PACKING") {
    payload.buyer_packing_text = String(form.buyer_packing_text || "").trim();
  }
  if (payload.delivery_condition === "OTHER") {
    payload.delivery_terms_other = String(form.delivery_terms_other || "").trim();
  }
  return payload;
}

export function sortPartiesByName(list) {
  return [...list].sort((a, b) => String(a.display_name).localeCompare(String(b.display_name)));
}

export function upsertParty(list, party) {
  const next = list.filter((p) => String(p.id) !== String(party.id));
  next.push(party);
  return sortPartiesByName(next);
}
